import type { FC } from 'react';
import React, { useContext } from 'react';
import {
  Box,
  CircularProgress,
  Divider,
  Stack,
  TextField,
} from '@mui/material';

import ErrorDisplay from 'src/components/chatbot/ChatbotChat/components/ErrorDisplay';
import MessageDisplay from 'src/components/chatbot/ChatbotChat/components/MessageDisplay';
import type { OpenAiContext } from 'src/components/chatbot/ChatbotChat/context/openAiContext';
import type { VirtualAssistantContext } from 'src/components/chatbot/ChatbotChat/context/virtualAssistantContext';
import { CHATBOT_ROLE } from 'src/components/chatbot/constants';

import ChatbotFeedbackButton from './components/FeedbackButton';

import { styles } from './index.styles';

export const preChatDisplayMsg = "What's the situation and what can I do?";

interface ChatbotChatProps {
  context: typeof VirtualAssistantContext | typeof OpenAiContext;
  placeholder?: string;
  showFeedbackForm?: boolean;
}

export const ChatbotChat: FC<ChatbotChatProps> = ({
  context,
  placeholder = '',
  showFeedbackForm,
}) => {
  const {
    chatDialog,
    chatHistoryRef,
    error,
    errorHandler,
    handleTextAreaKeyDown,
    isLoading,
    setInputValue,
    scrollToBottom,
  } = useContext(context);

  return (
    <Stack sx={styles.wrapper}>
      <Box sx={styles.chatHistoryWrapper}>
        <Box sx={styles.chatHistory} ref={chatHistoryRef}>
          {chatDialog.map(({ content, role, displayMessage, highlight }, i) => (
            <Box key={i}>
              <MessageDisplay
                content={content}
                role={role}
                displayMessage={displayMessage}
                highlight={highlight}
              />
              {showFeedbackForm &&
                !isLoading &&
                !displayMessage &&
                i + 1 === chatDialog.length && (
                  <ChatbotFeedbackButton scrollToBottom={scrollToBottom} />
                )}
            </Box>
          ))}
          {isLoading && (
            <MessageDisplay
              content={
                <Box sx={styles.spinner}>
                  <CircularProgress size={20} />
                </Box>
              }
              role={CHATBOT_ROLE}
            />
          )}
        </Box>
        {!!error.msg && (
          <ErrorDisplay errorMessage={error.msg} onClick={errorHandler} />
        )}
      </Box>
      <Box sx={styles.inputFieldArea}>
        <Divider />
        <Stack component="form" direction="row" spacing={1}>
          <TextField
            onKeyDown={handleTextAreaKeyDown}
            multiline
            type="text"
            name="chatInput"
            value={isLoading ? '' : undefined}
            onChange={(e) => setInputValue(e.target.value)}
            sx={styles.textField}
            placeholder={placeholder}
            fullWidth
            autoFocus
          />
        </Stack>
      </Box>
    </Stack>
  );
};

export default ChatbotChat;
